/* eslint-disable no-restricted-syntax */
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useApolloClient, useQuery } from "react-apollo";
import gql from "graphql-tag";
import { Button, Field, Control, Icon } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { OrdersTable } from "../../../../components/Orders";
import { UPDATE_ORDER_MUTATION } from "../../../../graphql/Orders/Orders.graphql";
import Loading from "../../../../components/Loading";
import Confirmation from "../../../../components/modals/Confirmation";
import { useModal } from "../../../../components/ModalProvider";

import {
  ourToast as toast,
  generateInvoiceBatchNumber,
  runPromisesInSeries,
  uniqueElementsBy
} from "../../../../utils";
import { useVoidInvoices } from "../../../../hooks";
import { BILLING_VERIFIED_INVOICE_COLUMNS } from "../columns";

const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const ALL_ORDERS_INV_GEN_QUERY = gql`
  query ALL_ORDERS_QUERY($where: OrderWhereInput!) {
    Orders(where: $where) {
      RecId
      InvoiceNumber
      InvoiceBatchNumber
      InvoiceDate
    }
  }
`;

const InvoiceGenerated = () => {
  const SystemCodes = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["Order"],
        CodeName_in: ["OrderStatus"],
        CodeId_in: ["BV", "INV", "EXA"]
      }
    }
  });
  const client = useApolloClient();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [States, setStates] = useState(null);
  const [selectedFlatRows, setSelectedFlatRows] = useState([]);
  const { setModalOpen } = useModal();

  const performVoidInvoices = useVoidInvoices();

  useEffect(() => {
    if (SystemCodes?.data?.SystemCodes?.length > 0 && !States) {
      setStates({
        BillingVerified: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "BV"
        ),
        InvoiceGenerated: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "INV"
        ),
        ExportedToAccounting: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "EXA"
        )
      });
    }
  }, [States, SystemCodes, SystemCodes.data]);

  const handleExportToAccounting = async selectedData => {
    try {
      if (!selectedData.length) {
        throw Error("Please select some orders to be exported");
      }
      setLoading(true);
      const exportDataToAccounting = async () => {
        const uniqueElements = uniqueElementsBy(
          selectedData,
          (a, b) => a === b
        );

        const { data: ordersData } = await client.query({
          query: ALL_ORDERS_INV_GEN_QUERY,
          variables: {
            where: {
              InvoiceNumber_in: uniqueElements,
              StatusRecIdRef: States.InvoiceGenerated.RecId
            }
          }
        });
        const InvoiceBatchNumber = await generateInvoiceBatchNumber(4);

        await runPromisesInSeries(
          ordersData.Orders.map(({ RecId }, index) => () => {
            const obj = {
              mutation: UPDATE_ORDER_MUTATION,
              variables: {
                data: {
                  InvoiceBatchNumber,
                  StatusRecIdRef: States.ExportedToAccounting.RecId
                },
                where: {
                  RecId
                }
              }
            };
            if (index === ordersData.Orders.length - 1) {
              obj.refetchQueries = ["ALL_ORDERS_VIEW_BY_PAGE_QUERY"];
            }
            return client.mutate(obj);
          })
        );

        toast.success("Orders exported successfully");
      };

      const message = `Are you sure to Export to Accounting for all invoices in this batch?`;

      setModalOpen(
        true,
        <Confirmation
          message={message}
          onCancel={() => {
            setModalOpen(false);
          }}
          onConfirm={async () => {
            await exportDataToAccounting();
            setModalOpen(false);
          }}
        />
      );
    } catch (err) {
      console.log(err.message);
      toast.error("Error exporting Orders.");
    } finally {
      setLoading(false);
    }
  };

  const printInvoices = selectedData => {
    if (selectedData.length > 0) {
      const {
        REACT_APP_REPORT_BASE_URL = "https://gdev.gravoc.com"
      } = process.env;
      const BASE_URL = `${REACT_APP_REPORT_BASE_URL}/ReportViewer/ReportViewer.aspx?Path=Invoice`;
      const REPORT_URL = `${BASE_URL}&Param=InvoiceNumber&ParamValue=${selectedData.join()}`;
      window.open(REPORT_URL);
      setSelectedFlatRows([]);
    } else {
      toast.error("Please select some invoice");
    }
  };

  if (loading || SystemCodes?.loading || !States?.InvoiceGenerated?.RecId) {
    return <Loading />;
  }

  const performVoidInvoiceNumber = async selectedData => {
    try {
      if (!selectedData.length) {
        throw Error("Please select some orders to be reset");
      }
      setLoading(true);

      await performVoidInvoices(selectedData);

      toast.success("Orders reset successfully");
    } catch (err) {
      toast.error(
        "Whoops! We encountered an error processing this request. Please contact support."
      );
    } finally {
      setLoading(false);
      setSelectedFlatRows([]);
    }
  };

  const handleVoidInvoices = async () => {
    try {
      const invoiceNumbers = [
        ...new Set(selectedFlatRows.map(x => x.InvoiceNumber))
      ];

      const { data: MatchingOrdersData } = await client.query({
        query: ALL_ORDERS_INV_GEN_QUERY,
        variables: {
          where: {
            StatusRecIdRef: States.InvoiceGenerated.RecId,
            InvoiceNumber_in: invoiceNumbers
          }
        }
      });

      const message = `Do you really want to void all invoices with invoice number(s) ${invoiceNumbers.join(
        ", "
      )}?`;

      setModalOpen(
        true,
        <Confirmation
          message={message}
          onCancel={() => {
            setModalOpen(false);
          }}
          onConfirm={async () => {
            await performVoidInvoiceNumber(MatchingOrdersData.Orders);
            setModalOpen(false);
          }}
        />
      );
    } catch (err) {
      toast.error(
        "Whoops! We encountered an error processing this request. Please contact support."
      );
    }
  };

  return (
    <OrdersTable
      columns={BILLING_VERIFIED_INVOICE_COLUMNS(false)}
      initialSortBy={[
        {
          id: "InvoiceNumber",
          desc: false
        },
        { id: "OrderNumber", desc: false }
      ]}
      pageHeadStart={
        <Field kind="group">
          <Control>
            <Button
              color="primary"
              disabled={!selectedFlatRows.length}
              size="small"
              onClick={() =>
                handleExportToAccounting(
                  selectedFlatRows.map(s => s.InvoiceNumber)
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="file-export" />
              </Icon>
              <span>Export To Accounting</span>
            </Button>
          </Control>
          <Control>
            <Button
              color="danger"
              disabled={!selectedFlatRows.length}
              size="small"
              onClick={handleVoidInvoices}
            >
              <Icon>
                <FontAwesomeIcon icon="exclamation-circle" />
              </Icon>
              <span>Void Invoice</span>
            </Button>
          </Control>
        </Field>
      }
      printInvoices={printInvoices}
      selectedFlatRows={selectedFlatRows}
      setSelectedFlatRows={setSelectedFlatRows}
      where={{ StatusRecIdRef: States.InvoiceGenerated.RecId }}
      onCellClick={cell => {
        if (cell.column.id !== "selection") {
          history.push(`/dispatch/orders/${cell.row.original.RecId}/BOL-ELD`);
        }
      }}
    />
  );
};
InvoiceGenerated.propTypes = {};
export default InvoiceGenerated;
